// ============================================================================
// Shared saved-view render-read registry
// ============================================================================
//
// Saved views are a reusable authenticated preference capability, not route
// data.  Several list hubs render the same picker, and before this registry
// each of them owned an eager `listSavedViews` effect of its own.  The finite
// receipt below records the physical mounts that now subscribe to the one
// scoped Query owner in savedViewsData.ts.
//
// This intentionally is not a routeDataCatalog entry and does not alter the
// 77-surface manifest.  A component using a saved-view picker is not thereby
// declared permanently owned or boot independent; this only owns the picker
// sidecar's automatic read.
// ============================================================================

export const SAVED_VIEWS_RESOURCE = Object.freeze({
  LIST: 'shared.saved-views.list',
});

// Cattle Dailys was already migrated by its own route-local resource before
// this foundation.  It is deliberately recorded here instead of being moved
// or duplicated: its query is already authenticated, scoped, and isolated from
// the daily-list retry key.  The shared owner below covers only former direct
// listSavedViews mount effects.
export const COMPATIBLE_SAVED_VIEWS_ROUTE_OWNERS = Object.freeze([
  Object.freeze({
    source: 'src/cattle/CattleDailysView.tsx',
    surface: 'cattle.dailys.index',
    resource: 'cattle.dailys.savedViews',
  }),
]);

export type SavedViewsConsumer = {
  readonly source: string;
  /** Surface(s) that can mount this one physical component. */
  readonly surfaces: readonly string[];
  /** app_saved_views surface_key values the component can request. */
  readonly surfaceKeys: readonly string[];
  /** The exact component expression passed to useSavedViewsData. */
  readonly surfaceKeyExpression: string;
};

// One entry per former component-local listSavedViews mount call (seventeen in
// this repository at migration time). The
// LivestockWeighInsView entry represents one physical call whose species prop
// selects either of its two real surface keys; it is deliberately not counted
// twice merely because the same mounted component can serve two routes.
//
// Fifteen remain. RETIRED under Design Constitution Q07 ("delete the hidden
// toolbars"):
//   - src/pig/PigBatchesView.tsx (surface pig.batches.index, key 'pig.batches',
//     PIG_BATCHES_SURFACE_KEY) — E4 (886980b3), pinned by pig_batches_filters_static.
//   - src/layer/LayerBatchesView.tsx (surface layer.batches.index, key
//     'layer.batches', LAYER_BATCHES_SURFACE_KEY) — E5/E12a, pinned by
//     layer_batches_filters_static.
// No data changes: any app_saved_views rows under those keys are untouched, and
// no client surface requests them now.
export const SAVED_VIEWS_CONSUMERS: readonly SavedViewsConsumer[] = Object.freeze([
  Object.freeze({
    source: 'src/broiler/BroilerDailysView.tsx',
    surfaces: Object.freeze(['broiler.dailys.index']),
    surfaceKeys: Object.freeze(['broiler.dailys']),
    surfaceKeyExpression: 'BROILER_DAILYS_SURFACE_KEY',
  }),
  Object.freeze({
    source: 'src/broiler/BroilerListView.tsx',
    surfaces: Object.freeze(['broiler.batches.index']),
    surfaceKeys: Object.freeze(['broiler.batches']),
    surfaceKeyExpression: 'BROILER_BATCHES_SURFACE_KEY',
  }),
  Object.freeze({
    source: 'src/cattle/CattleBatchesView.tsx',
    surfaces: Object.freeze(['cattle.batches.index']),
    surfaceKeys: Object.freeze(['cattle.batches']),
    surfaceKeyExpression: 'CATTLE_BATCHES_SURFACE_KEY',
  }),
  Object.freeze({
    source: 'src/cattle/CattleHerdsView.tsx',
    surfaces: Object.freeze(['cattle.herds.index']),
    surfaceKeys: Object.freeze(['cattle.herds']),
    surfaceKeyExpression: 'CATTLE_HERDS_SURFACE_KEY',
  }),
  Object.freeze({
    source: 'src/cattle/CattleWeighInsView.tsx',
    surfaces: Object.freeze(['view.cattleweighins']),
    surfaceKeys: Object.freeze(['cattle.weighins']),
    surfaceKeyExpression: 'CATTLE_WEIGHINS_SURFACE_KEY',
  }),
  Object.freeze({
    source: 'src/equipment/EquipmentFleetView.tsx',
    surfaces: Object.freeze(['equipment.fleet.index']),
    surfaceKeys: Object.freeze(['equipment.fleet']),
    surfaceKeyExpression: 'EQUIPMENT_FLEET_SURFACE_KEY',
  }),
  Object.freeze({
    source: 'src/equipment/EquipmentFuelLogView.tsx',
    surfaces: Object.freeze(['equipment.fleet.index']),
    surfaceKeys: Object.freeze(['equipment.fuelLog']),
    surfaceKeyExpression: 'EQUIPMENT_FUEL_LOG_SURFACE_KEY',
  }),
  Object.freeze({
    source: 'src/layer/EggDailysView.tsx',
    surfaces: Object.freeze(['layer.eggDailys.index']),
    surfaceKeys: Object.freeze(['layer.eggs']),
    surfaceKeyExpression: 'EGG_DAILYS_SURFACE_KEY',
  }),
  Object.freeze({
    source: 'src/layer/LayerDailysView.tsx',
    surfaces: Object.freeze(['layer.dailys.index']),
    surfaceKeys: Object.freeze(['layer.dailys']),
    surfaceKeyExpression: 'LAYER_DAILYS_SURFACE_KEY',
  }),
  Object.freeze({
    source: 'src/livestock/LivestockWeighInsView.tsx',
    surfaces: Object.freeze(['view.broilerweighins', 'view.pigweighins']),
    surfaceKeys: Object.freeze(['broiler.weighins', 'pig.weighins']),
    surfaceKeyExpression: 'savedViewSurfaceKey',
  }),
  Object.freeze({
    source: 'src/pig/PigDailysView.tsx',
    surfaces: Object.freeze(['pig.dailys.index']),
    surfaceKeys: Object.freeze(['pig.dailys']),
    surfaceKeyExpression: 'PIG_DAILYS_SURFACE_KEY',
  }),
  Object.freeze({
    source: 'src/sheep/SheepBatchesView.tsx',
    surfaces: Object.freeze(['sheep.batches.index']),
    surfaceKeys: Object.freeze(['sheep.batches']),
    surfaceKeyExpression: 'SHEEP_BATCHES_SURFACE_KEY',
  }),
  Object.freeze({
    source: 'src/sheep/SheepDailysView.tsx',
    surfaces: Object.freeze(['sheep.dailys.index']),
    surfaceKeys: Object.freeze(['sheep.dailys']),
    surfaceKeyExpression: 'SHEEP_DAILYS_SURFACE_KEY',
  }),
  Object.freeze({
    source: 'src/sheep/SheepFlocksView.tsx',
    surfaces: Object.freeze(['sheep.flocks.index']),
    surfaceKeys: Object.freeze(['sheep.flocks']),
    surfaceKeyExpression: 'SHEEP_FLOCKS_SURFACE_KEY',
  }),
  Object.freeze({
    source: 'src/sheep/SheepWeighInsView.tsx',
    surfaces: Object.freeze(['view.sheepweighins']),
    surfaceKeys: Object.freeze(['sheep.weighins']),
    surfaceKeyExpression: 'SHEEP_WEIGHINS_SURFACE_KEY',
  }),
]);
